'use client';

import { useEffect, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ExtendedDbCloudWalletUserInfo } from '@shared/types/db/user';
import { toast } from 'sonner';
import { useCopyToClipboard } from 'usehooks-ts';
import { IoMdCheckmark, IoMdClose } from 'react-icons/io';
import { Copy, Loader2 } from 'lucide-react';
import { IoCheckmark } from 'react-icons/io5';
import { BiEditAlt } from 'react-icons/bi';
import { useUserCloudQuery } from '@/hooks/onboarding/useReferralQuery';
import { _checkUsernameAvailability, _updateDbCloudInfo, _updateDbProfile, StagesProfileForm, UserFormSchema } from '@/hooks/auth/userForm';
import { avatars } from '@/components/userSettings/SelectPfp';
import useReferralCode from '@/hooks/onboarding/useReferralCode';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import SelectAvatar from '@/components/userSettings/dektop/SelectAvatar';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import LogoutBtn from '@/components/userSettings/LogoutBtn';
import AvatarSelection from './AvatarSelection';
export default function Account({
  userInfo
}: {
  userInfo: {
    data: ExtendedDbCloudWalletUserInfo;
  };
}) {
  const currentAvatar = avatars.find(ava => {
    return ava.file === userInfo?.data?.pfp;
  });
  const [stage, setStage] = useState(StagesProfileForm.default);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editUsername, setEditUsername] = useState(false);
  const [editReferral, setEditReferral] = useState(false);
  const [disableInput, setDisableInput] = useState(true);
  const [selected, setSelected] = useState(currentAvatar);
  const [, copy] = useCopyToClipboard();
  const [isCopied, setIsCopied] = useState(false);
  const {
    refetch: refetchUserCloudData
  } = useUserCloudQuery();
  const {
    referralData,
    handleUseReferralCode
  } = useReferralCode();
  const _onSubmitting = useMemo(() => stage === StagesProfileForm.isCheckingUsername || stage === StagesProfileForm.isUpdatingAccount ? true : false, [stage]);
  const form = useForm<z.infer<typeof UserFormSchema>>({
    resolver: zodResolver(UserFormSchema),
    defaultValues: {
      pfp: userInfo?.data.pfp,
      username: userInfo?.data.username,
      first_name: userInfo?.data.first_name,
      experience: userInfo?.data.crypto_experience,
      referral_code: referralData?.referrer ? referralData?.referrer : ''
    }
  });
  useEffect(() => {
    if (form.watch('first_name') !== userInfo?.data.first_name || form.watch('pfp') !== userInfo?.data.pfp) {
      setDisableInput(false);
    } else {
      setDisableInput(true);
    }
  }, [form.watch('first_name'), form.watch('pfp')]);
  const handleOnselectPfp = (_pfp: string) => {
    form.setValue('pfp', _pfp);
  };
  async function onSubmit(values: z.infer<typeof UserFormSchema>) {
    if (form.watch('first_name') === userInfo?.data.first_name && form.watch('pfp') === userInfo?.data.pfp) return;
    setStage(StagesProfileForm.isCheckingUsername);
    try {
      setStage(StagesProfileForm.isUpdatingAccount);
      const _userInfo: {
        user_id: number;
        first_name: string;
        pfp: string;
      } = {
        user_id: userInfo?.data.user_id,
        pfp: values.pfp,
        first_name: values.first_name
      };
      const _updateUser = await _updateDbProfile(_userInfo);
      if (_updateUser.status === 201) {
        toast.success('Account updated!');
        setStage(StagesProfileForm.default);
        setEditUsername(false);
        setDisableInput(true);
        refetchUserCloudData();
      }
      if (_updateUser.data.error) {
        throw new Error('Error saving your profile');
      }
    } catch (error) {
      toast.error('Something went wrong, try again', {
        duration: 1000
      });
      setStage(StagesProfileForm.default);
      console.log('onSubmit:ERROR', {
        error
      });
    }
  }
  const toggleModal = () => {
    if (_onSubmitting) return;
    setIsModalOpen(!isModalOpen);
  };
  const handleSelect = async avatar => {
    setSelected({
      file: avatar.file,
      name: avatar.name
    });
    handleOnselectPfp(avatar.file);
    setTimeout(() => toggleModal(), 250);
    await form.handleSubmit(onSubmit)();
  };
  const handleCopy = (text: string) => () => {
    copy(text).then(() => {
      setIsCopied(true);
      toast.info('Username Copied!');
      console.log('Copied!', {
        text
      });
      setTimeout(() => setIsCopied(false), 1000);
    }).catch(error => {
      console.error('Failed to copy!', error);
    });
  };
  return <>
      {!isModalOpen ? <div>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className='grid gap-2 space-y-3'>
              {/* pfp */}
              <FormField control={form.control} name='pfp' render={() => <FormItem>
                    <FormLabel className='text-[16px] w-full flex justify-start mb-4'>
                      Profile Picture
                    </FormLabel>

                    <FormControl>
                      <div className='flex flex-col items-center'>
                        <Label htmlFor='pfp' className='cursor-pointer'>
                          <SelectAvatar selected={selected} disabled={_onSubmitting} toggleModal={toggleModal} />
                        </Label>
                        <FormMessage />
                      </div>
                    </FormControl>
                  </FormItem>} />
              {/* username */}
              <FormField control={form.control} name='username' render={({
            field
          }) => <FormItem className='space-y-3 relative'>
                    <FormLabel className='text-[16px]'>Username</FormLabel>
                    <FormControl>
                      <Input {...field} className='border-none
                      
                      bg-genius-blue' disabled={true} />
                    </FormControl>
                    <FormMessage />
                    <Button className='bg-transparent md:hover:bg-transparent absolute right-2 top-[24px]' size='icon' onClick={handleCopy(userInfo.data.username)}>
                      {isCopied ? <IoMdCheckmark className='w-4 h-4 text-green-400' /> : <Copy className='w-4 h-4 text-genius-cream/50' />}
                    </Button>
                  </FormItem>} />
              {/* first - last name */}
              <div className='grid gap-2 w-full'>
                <FormLabel className='text-[16px]'>What should we call you?</FormLabel>
                <div className='gap-2 w-full relative'>
                  <FormField control={form.control} name='first_name' render={({
                field
              }) => <FormItem className='w-full'>
                        <FormControl className='w-full'>
                          <Input className='border-none bg-genius-blue w-full' disabled={!editUsername} placeholder='Nickname' {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                  {editUsername ? <div className='absolute top-[12px] right-4'>
                      <div className='flex items-center'>
                        <IoCheckmark onClick={async () => {
                    setEditUsername(false);
                    await form.handleSubmit(onSubmit)();
                  }} className='cursor-pointer w-[14px] text-green-400' />
                        <IoMdClose onClick={() => {
                    setEditUsername(false);
                    form.setValue('first_name', userInfo?.data.first_name);
                  }} className='cursor-pointer ml-2 w-[14px] text-rose-400' />
                      </div>
                    </div> : <BiEditAlt onClick={() => setEditUsername(true)} className='cursor-pointer absolute top-[12px] right-4 w-[14px]' />}
                </div>
              </div>
              <div className='grid gap-2 w-full '>
                <FormLabel className='text-[16px]'>
                  {referralData?.referrer ? 'Referred By' : 'Add Referral Code'}
                </FormLabel>
                {referralData?.referrer?.username ? <div className='text-genius-cream/50 text-sm'>
                    {referralData?.referrer?.username}
                  </div> : <div className=' gap-2 w-full relative'>
                    <FormField control={form.control} name='referral_code' render={({
                field
              }) => <FormItem className='w-full'>
                          <FormControl className='w-full'>
                            <Input className='border-none bg-genius-blue w-full' disabled={!editReferral || referralData?.referrer} placeholder={'Add Referral Code'} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />
                    {editReferral ? <div className='absolute top-[12px] right-4 '>
                        <div className='flex items-center'>
                          <IoCheckmark onClick={async () => {
                    setEditReferral(false);
                    const input = form.watch('referral_code');
                    if (input?.length !== 6) {
                      toast.error('Invalid Referral Code');
                      form.setValue('referral_code', '');
                      return;
                    }
                    const result = await handleUseReferralCode(input);
                    if (result) {
                      toast.success('Referral code applied successfully');
                    } else {
                      toast.error('Failed to activate referral code');
                    }
                  }} className='cursor-pointer w-[14px] text-green-400' />
                          <IoMdClose onClick={() => {
                    setEditReferral(false);
                    form.setValue('referral_code', '');
                  }} className='cursor-pointer ml-2 w-[14px] text-rose-400' />
                        </div>
                      </div> : <>
                        {!referralData?.referrer && <BiEditAlt onClick={() => setEditReferral(true)} className='cursor-pointer absolute top-[12px] right-4 w-[14px]' />}
                      </>}
                  </div>}
              </div>
              <Button className='hidden h-[56px] rounded-full bg-genius-pink hover:bg-genius-pink mb-4' type='submit' disabled={_onSubmitting || disableInput}>
                <div className='flex gap-2'>
                  {stage === StagesProfileForm.isCheckingUsername || stage === StagesProfileForm.isUpdatingAccount ? <Loader2 className='h-4 w-4 animate-spin' /> : null}
                  {stage === StagesProfileForm.isUpdatingAccount && <p>Saving Profile</p>}
                  {stage === StagesProfileForm.default && <p className=''>Continue</p>}
                </div>
              </Button>
            </form>
          </Form>
          <div className='w-full mt-[25%] lg:mt-[10%]'>
            <LogoutBtn />
          </div>
        </div> : <AvatarSelection toggleModal={toggleModal} handleSelect={handleSelect} selected={selected} />}
    </>;
}